import { sectionRenderer } from '@theme/section-renderer';

/**
 * SIGMAN: brinde automático no carrinho.
 *
 * Regra: o brinde entra quando o carrinho tem 2 peças ou mais, ou quando tem um dos kits.
 * Mantém exatamente 1 unidade do brinde nesses casos e retira o brinde quando a regra deixa de valer.
 * O id da variante do brinde e a lista de kits são definidos em snippets/sg-cart.liquid.
 */
const config = document.querySelector('script[data-sg-gift-variant]');
const giftId = Number(config?.dataset.sgGiftVariant);
const kitHandles = (config?.dataset.sgKitHandle || '')
  .split(',')
  .map((handle) => handle.trim())
  .filter(Boolean);
const minItems = Number(config?.dataset.sgGiftMin) || 2;
const root = window.Shopify?.routes?.root ?? '/';

let running = false;
let queued = false;
let addFailed = false;

/**
 * @param {string} url
 * @param {object} [body]
 */
const json = (url, body) =>
  fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then((response) => {
    if (!response.ok) throw new Error(`${url}: ${response.status}`);
    return response.json();
  });

/**
 * Redesenha o carrinho (gaveta e página) e o contador do ícone.
 * @param {number} itemCount
 */
async function refresh(itemCount) {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
  });

  await Promise.all([...sectionIds].map((id) => sectionRenderer.renderSection(id, { cache: false })));

  document.querySelectorAll('cart-icon').forEach((icon) => {
    if ('renderCartBubble' in icon && typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(itemCount, false, false);
    }
  });
}

async function sync() {
  if (running) {
    queued = true;
    return;
  }

  running = true;

  try {
    const cart = await json(`${root}cart.js`);
    const gifts = cart.items.filter((item) => item.variant_id === giftId);
    const others = cart.items.filter((item) => item.variant_id !== giftId);
    const giftQuantity = gifts.reduce((sum, item) => sum + item.quantity, 0);
    const paidQuantity = others.reduce((sum, item) => sum + item.quantity, 0);
    const hasKit = others.some((item) => kitHandles.includes(item.handle));
    const eligible = paidQuantity >= minItems || hasKit;
    let updated = null;

    if (eligible && giftQuantity === 0) {
      if (!addFailed) {
        try {
          await json(`${root}cart/add.js`, {
            items: [{ id: giftId, quantity: 1, properties: { _brinde: 'sim' } }],
          });
          updated = await json(`${root}cart.js`);
        } catch (error) {
          // Ex.: brinde sem estoque. Não insiste de novo nesta página.
          addFailed = true;
          console.warn('[sg-gift]', error);
        }
      }
    } else if (!eligible && giftQuantity > 0) {
      const updates = Object.fromEntries(gifts.map((item) => [item.key, 0]));
      updated = await json(`${root}cart/update.js`, { updates });
    } else if (giftQuantity > 1) {
      const updates = Object.fromEntries(gifts.map((item, index) => [item.key, index === 0 ? 1 : 0]));
      updated = await json(`${root}cart/update.js`, { updates });
    }

    if (updated) await refresh(updated.item_count);
  } catch (error) {
    console.warn('[sg-gift]', error);
  } finally {
    running = false;

    if (queued) {
      queued = false;
      sync();
    }
  }
}

if (Number.isFinite(giftId) && giftId > 0) {
  document.addEventListener('cart:update', () => setTimeout(sync, 0));

  if (config?.dataset.sgNeedsSync === 'true') sync();
}
